/**
 * RiskIntelligenceSummaryCard — hero verdict card in the Decision Intelligence column.
 *
 * Severity-driven (not count-driven) per the platform's Risk Severity Model:
 *   0 open indicators      → "No Risk Indicators Found"          (emerald · green)
 *   max = potential        → "Potential Risk Indicators Found"   (amber · yellow)
 *   max = significant      → "Significant Risk Indicators Found" (orange)
 *   max = elevated         → "Elevated Risk Indicators Found"    (rose · red — reserved)
 *
 * The colour reflects the materiality of the strongest finding, not merely the
 * number of items. Elevated (red) is reserved exclusively for confirmed or
 * highly credible evidence of fraud, identity issues, fabricated references,
 * or altered documents.
 *
 * Self-fetches the open risk-indicator list so it can be dropped into any
 * page without props plumbing. Renders directly between the NTN Recommendation
 * hero and the Risk Indicators alert cards.
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ManualSeverityPencil from "./ManualSeverityPencil";
import { ShieldCheck, ShieldAlert, AlertTriangle, AlertOctagon, HelpCircle, ArrowRight } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const STATE = {
  pending: {
    label: "Pending NTN Review",
    detail: "NTN staff has not yet assigned a risk value for this applicant.",
    Icon: HelpCircle,
    ringCls: "ring-2 ring-slate-200",
    iconWrap: "bg-slate-500 ring-slate-600",
    iconFg: "text-white",
    accentBar: "bg-slate-500",
    chip: "bg-slate-100 text-slate-700",
    bg: "bg-gradient-to-br from-slate-50/60 to-white",
  },
  none: {
    label: "No Risk Indicators Found",
    detail: "NTN has not flagged any observations for this applicant.",
    Icon: ShieldCheck,
    ringCls: "ring-2 ring-emerald-200",
    iconWrap: "bg-emerald-600 ring-emerald-700",
    iconFg: "text-white",
    accentBar: "bg-emerald-600",
    chip: "bg-emerald-100 text-emerald-800",
    bg: "bg-gradient-to-br from-emerald-50/50 to-white",
  },
  potential: {
    label: "Potential Risk Indicators Found",
    detail: "Minor inconsistencies or items to review — not a conclusion.",
    Icon: ShieldAlert,
    ringCls: "ring-2 ring-amber-300",
    iconWrap: "bg-amber-500 ring-amber-600",
    iconFg: "text-white",
    accentBar: "bg-amber-500",
    chip: "bg-amber-100 text-amber-900",
    bg: "bg-gradient-to-br from-amber-50/60 to-white",
  },
  significant: {
    label: "Significant Risk Indicators Found",
    detail: "Material inconsistencies warrant heightened scrutiny — not conclusive evidence of fraud.",
    Icon: AlertTriangle,
    ringCls: "ring-2 ring-orange-300",
    iconWrap: "bg-orange-600 ring-orange-700",
    iconFg: "text-white",
    accentBar: "bg-orange-600",
    chip: "bg-orange-100 text-orange-900",
    bg: "bg-gradient-to-br from-orange-50/60 to-white",
  },
  elevated: {
    label: "Elevated Risk Indicators Found",
    detail: "Confirmed or highly credible evidence of fraud, identity issues, fabricated references, or altered documents.",
    Icon: AlertOctagon,
    ringCls: "ring-2 ring-rose-300",
    iconWrap: "bg-rose-700 ring-rose-800",
    iconFg: "text-white",
    accentBar: "bg-rose-700",
    chip: "bg-rose-100 text-rose-800",
    bg: "bg-gradient-to-br from-rose-50/60 to-white",
  },
};

const SEV_RANK = { potential: 1, significant: 2, elevated: 3 };
const OPEN_STATUSES = new Set(["open", "under_review", "reviewed"]);

function maxSeverity(indicators) {
  let best = null;
  let bestRank = 0;
  for (const ri of indicators || []) {
    if (!OPEN_STATUSES.has(ri.status)) continue;
    const rank = SEV_RANK[ri.severity] || 0;
    if (rank > bestRank) { bestRank = rank; best = ri.severity; }
  }
  return best;
}

export default function RiskIntelligenceSummaryCard({
  screeningId,
  openCount: openCountProp,
  indicators: indicatorsProp,
  stored,          // Phase E.1: preferred source — { severity, label, ... }
  staffEndpoint,   // Phase E.1: enables the inline "Set risk" pencil (NTN only)
  onStoredSaved,   // Phase E.1: callback after staff saves
  eyebrow = "Risk Intelligence Summary",
  testid = "risk-intelligence-summary",
}) {
  const { user } = useAuth();
  const [openCount, setOpenCount] = useState(openCountProp ?? null);
  const [maxSev, setMaxSev] = useState(indicatorsProp ? maxSeverity(indicatorsProp) : null);

  useEffect(() => {
    // Parent-supplied indicators (household aggregate view) win — mirror them
    // and skip the network fetch entirely.
    if (indicatorsProp !== undefined) {
      const open = (indicatorsProp || []).filter((r) => OPEN_STATUSES.has(r.status));
      setOpenCount(open.length);
      setMaxSev(maxSeverity(indicatorsProp));
      return undefined;
    }
    if (openCountProp !== undefined && !screeningId) {
      // Legacy call site that only knows the count. Best-effort fall back to
      // the count-based tier so we still render something coherent.
      setOpenCount(openCountProp);
      setMaxSev(openCountProp === 0 ? null : "potential");
      return undefined;
    }
    if (!screeningId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/screenings/${screeningId}/risk-indicators`);
        if (cancelled) return;
        const open = (data || []).filter((r) => OPEN_STATUSES.has(r.status));
        setOpenCount(open.length);
        setMaxSev(maxSeverity(data));
      } catch {
        if (!cancelled) { setOpenCount(0); setMaxSev(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [screeningId, openCountProp, indicatorsProp]);

  if (openCount === null && !stored) {
    return (
      <div className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm p-6" data-testid={testid}>
        <div className="text-slate-300 text-sm">Loading risk intelligence…</div>
      </div>
    );
  }

  // Phase E.1 (2026-02): manual value ALWAYS wins. When absent, show
  // "Pending NTN Review" in neutral slate rather than falling back to
  // the indicator-derived value (the platform no longer displays that
  // derivation to PMs).
  const isStaff = user?.role === "ntn_staff";
  let key;
  if (stored && stored.severity) {
    key = stored.severity === "none" ? "none" : stored.severity;
  } else {
    key = "pending";
  }
  const s = STATE[key] || STATE.pending;
  const Icon = s.Icon;

  return (
    <section
      className={`relative ${s.bg} rounded-lg ${s.ringCls} shadow-md overflow-hidden border-l-4 ${s.accentBar.replace("bg-", "border-l-")}`}
      data-testid={testid}
    >
      <div className="p-5 flex items-start gap-5">
        <div className={`h-12 w-12 rounded-lg ${s.iconWrap} ring-2 flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon className={`h-6 w-6 ${s.iconFg}`} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="ntn-eyebrow flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${s.accentBar}`} />
            {eyebrow}
            {isStaff && staffEndpoint && (
              <ManualSeverityPencil
                endpoint={staffEndpoint}
                current={stored}
                onSaved={onStoredSaved}
                testid={`${testid}-set-risk`}
              />
            )}
          </div>
          <h2 className="ntn-h2 mt-2" data-testid={`${testid}-headline`}>
            {stored?.label || s.label}
          </h2>
          <p className="ntn-secondary mt-2 leading-relaxed">{s.detail}</p>
          {stored?.set_by_name && stored?.set_at && (
            <div className="mt-3 text-[11px] text-slate-500 font-mono" data-testid={`${testid}-attribution`}>
              Set by {stored.set_by_name} · {fmtDate(stored.set_at)}
            </div>
          )}
          {openCount > 0 && (
            <div className="mt-4 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ntn-eyebrow bg-slate-100 text-slate-700"
                    data-testid={`${testid}-count`}>
                {openCount} operational {openCount === 1 ? "indicator" : "indicators"} on record
              </span>
              <span className="ntn-caption inline-flex items-center gap-1">
                <ArrowRight className="h-3 w-3" /> see Risk Indicators below
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
